import { useEffect, useMemo, useState } from 'react';
import { InvoiceRequireApId } from 'src/constants/permissionsList';
import useBoolean from 'src/hooks/useBoolean';
import useDebounce from 'src/hooks/useDebounce';
import usePermission from 'src/hooks/usePermissions';
import { InvoiceDetailsResponse } from 'src/services/useInvoiceDetailsApi';
import useIsDuplicateApIdApi from 'src/services/useIsDuplicateApIdApi';

const useApIdErrors = (invoice: InvoiceDetailsResponse, isInvoiceEditDisabled: boolean) => {
  const [initialApId, setInitialApId] = useState('');
  const { value: isDirty, setTrue: setApIdDirty } = useBoolean();
  const [hasDuplicateApId, setHasDuplicateApId] = useState(false);
  const { execute: validateApId } = useIsDuplicateApIdApi(invoice?.poId?.toString());
  const debouncedApId = useDebounce(invoice?.apId?.toString().trim());
  const isApIdRequired = usePermission({ permissions: InvoiceRequireApId });

  useEffect(() => {
    if (!initialApId && invoice?.apId) {
      setInitialApId(invoice.apId);
    }
  }, [initialApId, invoice.apId]);

  useEffect(() => {
    if (debouncedApId && isDirty) {
      if (debouncedApId === initialApId) {
        setHasDuplicateApId(false);
      } else {
        validateApId(debouncedApId).then((hasDuplicateApId) => {
          setHasDuplicateApId(hasDuplicateApId);
        });
      }
    }
  }, [debouncedApId, initialApId, isDirty, validateApId]);

  const apIdErrors = useMemo(() => {
    if (
      invoice?.externalSupplier &&
      (!invoice || !invoice.apId || !invoice.apId.trim()) &&
      !isInvoiceEditDisabled
    ) {
      return ['AP ID cannot be empty which has External Supplier Name.'];
    }
    if (
      invoice?.supplierInheritance === 'NotInherited' ||
      invoice?.supplierInheritance === 'Inherited'
        ? !!invoice?.externalSupplier &&
          isApIdRequired &&
          (!invoice || !invoice.apId || !invoice.apId.trim()) &&
          !isInvoiceEditDisabled
        : isApIdRequired &&
          (!invoice || !invoice.apId || !invoice.apId.trim()) &&
          !isInvoiceEditDisabled
    ) {
      return ['Document requires AP ID.'];
    }

    if (invoice && invoice.apId && invoice.apId.trim().length > 30) {
      return ['AP ID is more than 30 characters.'];
    }

    return [];
  }, [invoice, isApIdRequired, isInvoiceEditDisabled]);
  const apIdWarnings = useMemo(
    () =>
      hasDuplicateApId && invoice?.apId?.trim() && !isInvoiceEditDisabled
        ? ['Duplicate AP ID']
        : [],
    [hasDuplicateApId, invoice?.apId, isInvoiceEditDisabled]
  );

  return { apIdErrors, apIdWarnings, setApIdDirty, isDirty };
};

export default useApIdErrors;
